/**
 * People and roster API client.
 *
 * Propose and confirm are separate calls, mirroring the server. There is no `setAssignment(state)`,
 * because offering one would let a caller express "the parish agrees" as a variation of "someone
 * suggested" — and the state is decided server-side from who is asking, never from the body.
 */

import type { AccessMode } from '@pius/contracts';

import { request } from '../../api/client';

export type RoleOrigin = 'constraint' | 'template' | 'local';
export type AssignmentState = 'proposed' | 'confirmed';

export interface RosterAssignment {
  id: string;
  person_id: string;
  person_name: string;
  state: AssignmentState;
  note: string | null;
  confirmed_at: string | null;
  /** A name for an Account; "An anonymous participant" for a Capability, which has no name. */
  proposed_by: string | null;
}

export interface RosterRole {
  id: string;
  role_key: string;
  label: string;
  origin: RoleOrigin;
  allows_multiple: boolean;
  in_use: boolean;
  /** Decided by the Selection Policy, never by the Template. */
  access_mode: AccessMode;
  /** Only a Local Role is the roster's to remove. */
  removable: boolean;
  policy_note?: string;
  assignments: RosterAssignment[];
}

export interface Person {
  id: string;
  display_name: string;
  contact: string | null;
}

export interface RosterView {
  may_manage: boolean;
  roles_in_use: RosterRole[];
  roles_available: RosterRole[];
  /** Always empty on an Ordinary Form Template. Enabling the form is a Template decision. */
  roles_extraordinary_form: RosterRole[];
  people: Person[];
}

export function fetchRoster(liturgyId: string): Promise<RosterView> {
  return request(`/liturgies/${liturgyId}/people`);
}

export function addPerson(
  liturgyId: string,
  input: { display_name: string; contact?: string | null },
): Promise<Person> {
  return request(`/liturgies/${liturgyId}/people`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function addLocalRole(
  liturgyId: string,
  input: { label: string; allows_multiple: boolean },
): Promise<{ id: string; label: string; origin: RoleOrigin }> {
  return request(`/liturgies/${liturgyId}/roles`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function removeRole(liturgyId: string, roleId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/roles/${roleId}/remove`, { method: 'POST' });
}

export function restoreRole(liturgyId: string, roleId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/roles/${roleId}/restore`, { method: 'POST' });
}

/** The resulting state comes back from the server; it is never sent. */
export function assignPerson(
  liturgyId: string,
  roleId: string,
  input: { person_id: string; note?: string | null },
): Promise<{ id: string; state: AssignmentState }> {
  return request(`/liturgies/${liturgyId}/roles/${roleId}/assignments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function confirmAssignment(
  liturgyId: string,
  assignmentId: string,
): Promise<{ id: string; state: AssignmentState }> {
  return request(`/liturgies/${liturgyId}/assignments/${assignmentId}/confirm`, {
    method: 'POST',
  });
}

export function unassign(liturgyId: string, assignmentId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/assignments/${assignmentId}`, { method: 'DELETE' });
}
